/**
 * Copy for the onboarding checklist "Learn more" modals.
 *
 * Source of truth: docs/Signalist — Onboarding Progress Copy.pdf.
 * The modal presentation mirrors the `LEARN_CONTENT` object in the
 * `signalist-app-v22` prototype (icon + title + subtitle + HTML body + CTA).
 *
 * `body` is an HTML string rendered by the modal; keep the inline tags
 * (<p>, <ul>, <li>, <strong>, <em>, <h4>) so the styled body renders them.
 */
export interface LearnTopic {
	title: string;
	sub: string;
	/** HTML string rendered inside the modal body. */
	body: string;
	cta: string;
	/** Inline SVG markup shown in the modal header icon. */
	icon: string;
	/** Optional YouTube video id for the in-app walkthrough player. */
	videoId?: string;
}

const ICONS = {
	list: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>',
	link: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
	eye: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.5 12c0-4-3.5-7.5-7.5-7.5S4.5 8 4.5 12"/><path d="M19.5 12c0 4-3.5 7.5-7.5 7.5S4.5 16 4.5 12"/></svg>',
	star: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
	search:
		'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>',
	sparkle:
		'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/></svg>',
	send: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',
	crm: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>',
};

export const LEARN_CONTENT: Record<string, LearnTopic> = {
	"connect-accounts": {
		title: "Connect your LinkedIn & email accounts",
		sub: "Build multi-channel campaigns and reply from the Unified inbox",
		body: `<p>You can connect your Linkedin account, primary emails and secondary emails. To keep your accounts safe we are using Unipile, the leading infrastructure provider for Linkedin and email messaging. When connecting you'll notice a popup to enter your credentials. <strong>Signalist never sees your login credentials and password!</strong></p>
<p>Once connected with Linkedin, Signalist can:</p>
<ul>
  <li>Send LinkedIn messages and connection requests on your behalf.</li>
  <li>Mark your existing Linkedin connections on all lead lists.</li>
  <li>Build lead lists based on Profile Visitors, Company Followers or import Linkedin Search and Sales Navigator lists and a few others.</li>
</ul>
<p>Reconnection is rare but happens occasionally if you change your password. We will alert you immediately if a new connection is required.</p>`,
		cta: "Connect",
		icon: ICONS.link,
	},
	"connect-crm": {
		title: "Connect your CRM to Signalist",
		sub: "Check lead lists for existing CRM records and create new leads automatically",
		body: `<p>Connecting your CRM lets you easily see on any lead list whether the lead or the company already is in your CRM or not. You can also automatically create new records — for example if a lead has replied — and create deals automatically or manually, even including custom AI-fields.</p>
<ul>
  <li><strong>Check lead lists</strong> — Signalist automatically checks any lead list for existing CRM records.</li>
  <li><strong>Automatic creation</strong> — automatically create new leads, contacts, companies or deals when leads get contacted or reply.</li>
  <li><strong>Custom AI fields</strong> — use AI prompts to automatically research custom fields like fleet size, internal Business Unit fields, annual revenue and much more.</li>
</ul>
<p>Currently we support HubSpot, Pipedrive and Zoho. Salesforce and MS Dynamics will be next.</p>`,
		cta: "Connect",
		icon: ICONS.crm,
		videoId: "_qEoxkkLETI",
	},
	"lead-scoring": {
		title: "Review the Lead Scoring",
		sub: "Lead lists get automatically segmented and scored to indicate a Lead Priority",
		body: `<p>Every lead gets a score based on the Company Score and Persona Score so your team focuses time where it matters. In the background, three prompts analyze each lead:</p>
<ul>
  <li><strong>Company Segment</strong> — Is the company relevant for me and which segment do they belong to?</li>
  <li><strong>Persona Segment</strong> — Is the job title relevant for me and which segment is it?</li>
  <li><strong>Lead Score</strong> — Every lead gets marked as Prio 1, Prio 2, Prio 3 or Disqualified based on firmographic and AI-based criteria.</li>
</ul>
<p>This helps you filter lead lists easily. You can overrule this or change the segment and score per lead and help Signalist get smarter over time.</p>
<p>Calibrate the pre-configured Lead Scoring and pay close attention to the reasoning per lead when reviewing the first lead lists — tweak and re-score if necessary.</p>`,
		cta: "Review scoring",
		icon: ICONS.star,
		videoId: "ByFK8YAsRXI",
	},
	"first-list": {
		title: "Set up your first lead lists",
		sub: "Create one-time or running lead lists easily based on a variety of signals",
		body: `<p>Every signal or list type has a short setup — sometimes it's just one click. Explore and try out which signals make most sense for you.</p>
<p>Most teams use a couple of one-time lists and running lists in parallel.</p>
<ul>
  <li><strong>One-time lists</strong> — e.g. for events and conferences or lead database search.</li>
  <li><strong>Running lists</strong> — e.g. for new jobs, hires, startup funding etc.</li>
  <li><strong>Profile Trackers</strong> are slightly different — you actively define the leads or companies you want to monitor for all signals we can find online.</li>
</ul>
<p>You can always add more later — the dashboard's "Create new list" bar is always one click away.</p>`,
		cta: "Create a list",
		icon: ICONS.list,
	},
	"profile-tracker": {
		title: "Add leads to the Profile Tracker",
		sub: "Observe job changes and custom signals at the accounts that matter most",
		body: `<p>The <strong>Profile Tracker</strong> constantly watches for new signals on profiles you have added. You can import a new list, add manually, or add people and/or companies from any other list.</p>
<p>Once done, we use the defined interval to scan the whole web — including Linkedin posts from the people and company pages — and classify whether this is relevant for you or not.</p>
<p>Next to the Lead Score you will also see a Signal score, which you can override.</p>
<p>New Signals can be reviewed via the list, or you move to the Feed to scroll through relevant news that matters to you.</p>`,
		cta: "Add leads",
		icon: ICONS.eye,
		videoId: "XPNTLwE09_A",
	},
	"deep-research-prompt": {
		title: "Review the Deep Research Prompt",
		sub: "Deep Research surfaces Talking Points and Insights you and the AI copywriting agent can leverage",
		body: `<p>The Deep Research Prompt is basically a prompt template that you can adjust and activate for every lead very easily and mark leads as Strong Fit, Medium Fit or Weak Fit. You can tailor the prompt completely to your needs and create multiple ones.</p>
<p>When the Lead Scoring defines the ICP Fit, the Deep Research Prompt goes much deeper and should focus more on the timing fit and help you prepare meetings.</p>
<p>It's also the basis for the Copywriting agent when it creates drafts. Try it out, review and improve the prompt as needed.</p>`,
		cta: "Review prompt",
		icon: ICONS.search,
	},
	"first-deep-research": {
		title: "Run Deep Research on leads",
		sub: "Try it out to see which Talking Points and Insights will be generated",
		body: `<p>Run Deep Research for a few leads and get a feeling for what helps you and the AI Copywriting agent to surface as insights.</p>
<p>What to watch for:</p>
<ul>
  <li><strong>Strong Fit</strong> — is this what a good lead would look like for you?</li>
  <li><strong>Insights</strong> — is the info truly relevant and helpful for you?</li>
  <li><strong>Talking Points, Why Us & Why now</strong> — does it resonate to use those talking points?</li>
</ul>
<p>If something's off, refine the prompt and run again if you wish to do so.</p>`,
		cta: "Run deep research",
		icon: ICONS.sparkle,
		videoId: "MOQuID5lqHM",
	},
	"first-campaign": {
		title: "Create your first campaign",
		sub: "Create multi-channel campaigns with or without AI variables",
		body: `<p>A campaign consists of steps, conditions and text variables. You can create multiple campaigns per list and copy them over from previous lists.</p>
<ul>
  <li><strong>Go to your List settings.</strong></li>
  <li><strong>Create a campaign</strong> — check steps, conditions and copy.</li>
  <li><strong>Add leads to campaigns</strong> — activate outreach and add leads into your campaign.</li>
</ul>`,
		cta: "Create campaign",
		icon: ICONS.send,
		videoId: "6dyRScwXCnk",
	},
};
